import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

// The five design-system statuses (SKE-4124) render the DS treatment: a tinted
// surface, no border, and a 2px inset accent bar down the left. The bar is a
// pseudo-element so the component's DOM contract is unchanged — every existing
// caller keeps passing the same children. `default` and `destructive` are the
// pre-DS variants and are deliberately left alone: 13 files still use them, and
// restyling them here would silently restyle unrelated surfaces.
const alertVariants = cva(
  "relative w-full rounded-lg border px-4 py-3 text-sm [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-neutral-900 [&>svg~*]:pl-7",
  {
    variants: {
      variant: {
        default: "bg-white text-neutral-900",
        destructive: "border-red-500/50 text-red-500 [&>svg]:text-red-500",
        info: "border-0 bg-blue-50 p-2 pl-[22px] text-neutral-800 before:absolute before:inset-y-2 before:left-2 before:w-0.5 before:rounded-[3px] before:content-[''] before:bg-blue-500",
        success:
          "border-0 bg-green-50 p-2 pl-[22px] text-neutral-800 before:absolute before:inset-y-2 before:left-2 before:w-0.5 before:rounded-[3px] before:content-[''] before:bg-green-600",
        warning:
          "border-0 bg-yellow-50 p-2 pl-[22px] text-neutral-800 before:absolute before:inset-y-2 before:left-2 before:w-0.5 before:rounded-[3px] before:content-[''] before:bg-yellow-600",
        error:
          "border-0 bg-red-50 p-2 pl-[22px] text-neutral-800 before:absolute before:inset-y-2 before:left-2 before:w-0.5 before:rounded-[3px] before:content-[''] before:bg-red-500",
        empty:
          "border-0 bg-neutral-50 p-2 pl-[22px] text-neutral-800 before:absolute before:inset-y-2 before:left-2 before:w-0.5 before:rounded-[3px] before:content-[''] before:bg-neutral-500",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

// Glyph + colour pairing per DS status, so the five statuses can't drift apart at
// call sites. Font Awesome Regular matches the design system's icon style.
const ALERT_ICON: Record<string, { glyph: string; color: string }> = {
  info: { glyph: "fa-circle-info", color: "text-blue-500" },
  success: { glyph: "fa-circle-check", color: "text-green-600" },
  warning: { glyph: "fa-triangle-exclamation", color: "text-yellow-600" },
  error: { glyph: "fa-circle-xmark", color: "text-red-500" },
  empty: { glyph: "fa-circle-info", color: "text-neutral-500" },
};

// The status icon that leads an Alert title row: a 20px box around a 16px glyph,
// matching the design system's icon geometry.
export function AlertIcon({
  variant,
  className,
}: {
  variant: keyof typeof ALERT_ICON;
  className?: string;
}) {
  const icon = ALERT_ICON[variant] ?? ALERT_ICON.info;
  return (
    <span
      className={cn(
        "inline-flex size-5 shrink-0 items-center justify-center",
        className
      )}
      aria-hidden="true"
    >
      <i className={`fa-regular ${icon.glyph} text-base ${icon.color}`} />
    </span>
  );
}

// Text column of a DS alert. Title, subtitle and CTAs all align to the icon's left
// edge (they are siblings of the title row, not indented past the icon).
export function AlertBody({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-1 py-1", className)}>{children}</div>
  );
}

// Inline CTA inside a DS alert: 12px blue medium text, optionally with a trailing
// icon. Rendered as a real button so it stays keyboard-reachable.
export function AlertCta({
  onClick,
  disabled,
  icon,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  icon?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1 rounded text-xs font-medium text-blue-500 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
    >
      {children}
      {icon && (
        <span
          className="inline-flex size-[15px] shrink-0 items-center justify-center"
          aria-hidden="true"
        >
          <i className={`fa-regular ${icon} text-xs`} />
        </span>
      )}
    </button>
  );
}

// Title row of a DS alert: status icon + medium-weight 14px text.
export function AlertTitleRow({
  variant,
  children,
  className,
}: {
  variant: keyof typeof ALERT_ICON;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <AlertIcon variant={variant} />
      <div className="min-w-0 flex-1 text-sm font-medium text-neutral-800">
        {children}
      </div>
    </div>
  );
}

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
));
Alert.displayName = "Alert";

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn("mb-1 font-medium leading-none tracking-tight", className)}
    {...props}
  />
));
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm [&_p]:leading-relaxed", className)}
    {...props}
  />
));
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertTitle, AlertDescription };
